import { Dashboard, Status } from "../../types";
import { Schemas } from "../../schemas";
import { serviceMapService } from "./serviceMapService";
import { sigv4ApiClient } from "./internal/sigv4ApiClient";
import { createEntityId } from "./internal/entityId";

const API_PATH = "/v1/dashboard";

const clampInt = (value: number): number => {
  const rounded = Math.round(value);
  if (rounded < 0) {return 0;}
  if (rounded > 100) {return 100;}
  return rounded;
};

const scoreFromStatus = (status: Status): number => {
  if (status === "healthy") {return 100;}
  if (status === "warning") {return 60;}
  return 35;
};

const riskFromStatus = (status: Status): number => {
  if (status === "healthy") {return 10;}
  if (status === "warning") {return 55;}
  return 85;
};

const loadAll = async (): Promise<Dashboard[]> => sigv4ApiClient.list<Dashboard>(API_PATH);

const saveOne = async (dashboard: Dashboard): Promise<Dashboard> =>
  sigv4ApiClient.put<Dashboard>(API_PATH, dashboard);

const computeMetricsByServiceMapId = async (
  serviceMapId: string,
): Promise<Dashboard["metrics"]> => {
  const serviceMap = await serviceMapService.getWithComputedStatuses(serviceMapId);
  const services = serviceMap.nodes.map((node) => node.data);

  if (services.length === 0) {
    return {
      platformHealth: 0,
      activeEvents: 0,
      riskTrending: 0,
    };
  }

  let scoreTotal = 0;
  let activeEvents = 0;
  let riskTrending = 0;

  for (const service of services) {
    const score =
      typeof service.metrics?.score === "number"
        ? clampInt(service.metrics.score)
        : scoreFromStatus(service.status);
    const risk =
      typeof service.metrics?.riskForecast === "number"
        ? clampInt(service.metrics.riskForecast)
        : riskFromStatus(service.status);
    const activeAlerts = service.metrics?.activeAlerts ?? 0;

    scoreTotal += score;
    activeEvents += Math.max(0, Math.round(activeAlerts));
    if (risk >= 60) {
      riskTrending += 1;
    }
  }

  return {
    platformHealth: clampInt(scoreTotal / services.length),
    activeEvents,
    riskTrending,
  };
};

const metricsAreEqual = (
  a: Dashboard["metrics"],
  b: Dashboard["metrics"],
): boolean => {
  return (
    a.platformHealth === b.platformHealth &&
    a.activeEvents === b.activeEvents &&
    a.riskTrending === b.riskTrending
  );
};

const refreshDashboardsWithComputedMetrics = async (): Promise<Dashboard[]> => {
  const dashboards = await loadAll();
  const next = await Promise.all(
    dashboards.map(async (dashboard) => {
      const computedMetrics = await computeMetricsByServiceMapId(
        dashboard.id,
      );
      return {
        ...dashboard,
        metrics: computedMetrics,
      };
    }),
  );

  const changed =
    next.length !== dashboards.length ||
    next.some((d, idx) => !metricsAreEqual(d.metrics, dashboards[idx].metrics));

  if (changed) {
    await Promise.all(next.map((dashboard) => saveOne(dashboard)));
  }

  return next;
};

export const dashboardService = {
  create: async (
    dashboard: Omit<Dashboard, "id" | "metrics">,
  ): Promise<Dashboard> => {
    const id = createEntityId();

    const created: Dashboard = Schemas.dashboard.parse({
      ...dashboard,
      id,
      metrics: {
        platformHealth: 0,
        activeEvents: 0,
        riskTrending: 0,
      },
    });
    await saveOne(created);

    await serviceMapService.create(id);

    const refreshed = await refreshDashboardsWithComputedMetrics();
    return refreshed.find((d) => d.id === created.id) ?? created;
  },
  list: async (): Promise<Dashboard[]> => {
    return refreshDashboardsWithComputedMetrics();
  },
  get: async (id: string): Promise<Dashboard | undefined> => {
    return (await refreshDashboardsWithComputedMetrics()).find((m) => m.id === id);
  },
  update: async (
    id: string,
    next: Omit<Dashboard, "id">,  ): Promise<Dashboard> => {
    const dashboards = await loadAll();
    const idx = dashboards.findIndex((m) => m.id === id);

    if (idx === -1) {
      throw new Error(`Dashboard with id "${id}" not found`);
    }

    const updated: Dashboard = { ...next, id };

    const updatedList = [...dashboards];
    updatedList[idx] = updated;

    await saveOne(updated);
    const refreshed = await refreshDashboardsWithComputedMetrics();
    const updatedDashboard = refreshed.find((dashboard) => dashboard.id === id);
    if (!updatedDashboard) {
      throw new Error(`Dashboard with id "${id}" not found after update`);
    }
    return updatedDashboard;
  },
  patch: async (
    id: string,
    patch: Partial<Omit<Dashboard, "id">>,
  ): Promise<Dashboard> => {
    const dashboards = await loadAll();
    const idx = dashboards.findIndex((m) => m.id === id);

    if (idx === -1) {
      throw new Error(`Dashboard with id "${id}" not found`);
    }

    const current = dashboards[idx];
    const updated: Dashboard = { ...current, ...patch, id };

    const updatedList = [...dashboards];
    updatedList[idx] = updated;

    await saveOne(updated);
    const refreshed = await refreshDashboardsWithComputedMetrics();
    const updatedDashboard = refreshed.find((dashboard) => dashboard.id === id);
    if (!updatedDashboard) {
      throw new Error(`Dashboard with id "${id}" not found after patch`);
    }
    return updatedDashboard;
  },
  delete: async (id: string): Promise<boolean> => {
    await sigv4ApiClient.delete(`${API_PATH}/${encodeURIComponent(id)}`);
    return true;
  },
};
